/**
 * WordDefinitionModal Component
 * 
 * Modal displaying the definition of a word when clicked in search results.
 * Fetches the definition from the API and shows it in a popup.
 * 
 * Features:
 * - Shows word, length, and definition
 * - Loading state while fetching
 * - "No definition" message if not found
 * - Close button and click-outside to close
 * - Always visible in current viewport position (uses React Portal)
 */

import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '../../hooks/useTranslation';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface WordDefinitionModalProps {
  /** The word to show definition for */
  word: string | null;
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
}

interface Definition {
  word: string;
  definition: string;
  source: string | null;
  language: string;
}

export function WordDefinitionModal({ word, isOpen, onClose }: WordDefinitionModalProps) {
  const { t } = useTranslation();
  const [definition, setDefinition] = useState<Definition | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch definition when word changes
  useEffect(() => {
    if (!word || !isOpen) {
      setDefinition(null);
      setError(null);
      return;
    }

    const fetchDefinition = async () => {
      setIsLoading(true);
      setError(null);
      setDefinition(null);

      try {
        const response = await fetch(`${API_BASE_URL}/config/definition/${encodeURIComponent(word)}`);
        const data = await response.json();

        if (data.success && data.data) {
          setDefinition(data.data);
        } else {
          setError(t('search.definition.notFound'));
        }
      } catch (err) {
        console.error('Failed to fetch definition:', err);
        setError(t('search.definition.error'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchDefinition();
  }, [word, isOpen, t]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Handle backdrop click
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  if (!isOpen || !word) return null;

  // Use React Portal to render modal at document body level
  // This ensures fixed positioning works correctly regardless of parent transforms
  const modalContent = (
    <div
      style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0,
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="definition-modal-title"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div 
        style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.5)',
          backdropFilter: 'blur(4px)'
        }}
      />

      {/* Modal content - centered in viewport */}
      <div 
        className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-lg w-full mx-4 p-6 animate-scale-in max-h-[80vh] overflow-y-auto"
        style={{ position: 'relative', zIndex: 1 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          aria-label={t('common.close')}
        >
          <CloseIcon className="w-5 h-5" />
        </button>

        {/* Word header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/40 rounded-full flex items-center justify-center">
            <span className="text-2xl">📖</span>
          </div>
          <div>
            <h2
              id="definition-modal-title"
              className="text-xl font-bold text-slate-900 dark:text-white"
            >
              {word}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {word.length} {t('search.letters')}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="min-h-[100px]">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-4 border-primary-200 dark:border-primary-800 border-t-primary-500 rounded-full animate-spin" />
            </div>
          )}

          {!isLoading && error && (
            <div className="text-center py-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center">
                <span className="text-3xl">📚</span>
              </div>
              <p className="text-slate-600 dark:text-slate-400">{error}</p>
            </div>
          )}

          {!isLoading && definition && (
            <div className="space-y-4">
              <div className="bg-slate-50 dark:bg-slate-700 rounded-xl p-4">
                <p className="text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
                  {definition.definition}
                </p>
              </div>
              
              {definition.source && (
                <p className="text-xs text-slate-400 dark:text-slate-500 text-right">
                  {t('search.definition.source')}: {definition.source}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-700">
          <button
            type="button"
            onClick={onClose}
            className="w-full px-4 py-3 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 font-medium rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );

  // Render modal at document body level using React Portal
  return createPortal(modalContent, document.body);
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

export default WordDefinitionModal;
